import { useEffect, useMemo, useState } from "react";
import Modal from "@mui/material/Modal";
import { Box, Typography, IconButton, Divider, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { API } from "../../../api";
import {
  formatPaymentAmount,
  hasStoredPaymentDetails,
  resolvePaymentDetails,
} from "../../../utils/paymentDisplay";

const modalBox = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 560,
  maxWidth: "calc(100vw - 32px)",
  maxHeight: "90vh",
  background: "#fff",
  borderRadius: "12px",
  outline: "none",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const header = {
  background: "#126ABC",
  padding: "16px 20px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const labelSx = { fontSize: "12px", color: "#888", fontWeight: 500, mb: 0.5 };
const valueSx = { fontSize: "14px", color: "#222", fontWeight: 500, wordBreak: "break-all" };

function DetailRow({ label, value, monospace = false }) {
  if (value == null || value === "" || value === "—") return null;
  return (
    <Box>
      <Typography sx={labelSx}>{label}</Typography>
      <Typography sx={{ ...valueSx, fontFamily: monospace ? "monospace" : "inherit" }}>
        {value}
      </Typography>
    </Box>
  );
}

const isImage = (url) => /\.(jpg|jpeg|png|gif|webp|bmp)(\?|$)/i.test(url || "");

export default function PaymentDetailsViewModal({ open, onClose, order }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !order?.id) return;
    setLoading(true);
    API.getPayments(order.id)
      .then((res) => {
        const body = res?.data?.data ?? res?.data ?? {};
        setPayments(Array.isArray(body?.payments) ? body.payments : []);
      })
      .catch(() => setPayments([]))
      .finally(() => setLoading(false));
  }, [open, order?.id]);

  const details = useMemo(
    () => resolvePaymentDetails(order, payments),
    [order, payments]
  );

  const hasAny = hasStoredPaymentDetails(order, payments);

  const handleClose = () => {
    setPayments([]);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalBox}>
        <Box sx={header}>
          <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "16px" }}>
            Payment Details
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: "#fff", p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: "24px", overflowY: "auto", flex: 1 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : !hasAny ? (
            <Typography sx={{ color: "#888", fontSize: "14px", textAlign: "center", py: 3 }}>
              No payment details found.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <DetailRow label="Invoice ID" value={details.invoiceId} monospace />
              <DetailRow label="Transaction ID" value={details.transactionId} monospace />
              <DetailRow label="Payment Mode" value={details.paymentMode} />
              <DetailRow
                label="Payment Amount"
                value={formatPaymentAmount(details.paymentAmount)}
              />
              <DetailRow label="Payment Date" value={details.paymentDate} />

              {(details.paymentProof || details.referenceNumber || details.paymentNote) && (
                <Divider />
              )}

              {details.paymentProof && (
                <Box>
                  <Typography sx={labelSx}>Payment Proof</Typography>
                  {isImage(details.paymentProof) ? (
                    <Box
                      component="a"
                      href={details.paymentProof}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      <Box
                        component="img"
                        src={details.paymentProof}
                        alt="Payment proof"
                        sx={{
                          maxWidth: "100%",
                          maxHeight: 260,
                          borderRadius: "8px",
                          border: "1px solid #e0e0e0",
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      component="a"
                      href={details.paymentProof}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                        color: "#126ABC",
                        fontSize: "14px",
                        fontWeight: 500,
                        textDecoration: "none",
                        border: "1px solid #126ABC",
                        borderRadius: "8px",
                        px: 2,
                        py: 1,
                        mt: 0.5,
                        "&:hover": { background: "#f0f6ff" },
                      }}
                    >
                      View Uploaded File
                    </Box>
                  )}
                </Box>
              )}

              <DetailRow label="Reference Number" value={details.referenceNumber} monospace />
              <DetailRow label="Note" value={details.paymentNote} />
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
